/**
 * Assessments — Convex-backed React hooks.
 *
 * Replaces assessments.queries.ts and assessments.mutations.ts.
 * Uses usePaginatedQuery for lists (Load More pagination) and
 * useMutation for write operations.
 */

import { useCallback } from 'react'
import { useMutation, usePaginatedQuery, useQuery } from 'convex/react'
import { api } from '@backend/_generated/api'
import type { Id } from '@backend/_generated/dataModel'

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ConvexUserSummary {
  _id: string
  name: string | null
  email: string | null
}

export interface ConvexPosition {
  _id: string
  title: string
  level: string
  role: string
}

export interface ConvexAssessmentType {
  _id: string
  name: string
  positionId: string
  position: ConvexPosition | null
}

export interface ConvexAssessmentResponse {
  _id: string
  userId: string
  responseType: string
  completedAt: number | null
  comment: string | null
  /** Last saved page index — enables cross-device resume without localStorage. */
  draftPage: number | null
}

export interface ConvexAssessmentListItem {
  _id: string
  employeeUserId: string
  managerUserId: string
  organizationId: string
  employee: ConvexUserSummary
  manager: ConvexUserSummary
  assessmentType: ConvexAssessmentType | null
  responses: ConvexAssessmentResponse[]
  createdAt: number | null
}

export interface ConvexAssessmentFull extends ConvexAssessmentListItem {
  period: { _id: string; startsAt: number; endsAt: number } | null
}

export interface ConvexQuestion {
  _id: string
  name: string
  subName: string
  responseType: string
  directReportText: string
  managerText: string
}

export interface ConvexAssessmentForSurvey extends ConvexAssessmentFull {
  assessmentType:
    | (ConvexAssessmentType & { questions: ConvexQuestion[] })
    | null
}

export interface ConvexAssessmentAverage {
  responseType: string
  overallAvg: number | null
  talentAvg: number | null
  craftAvg: number | null
  resultsAvg: number | null
  directionAvg: number | null
  cultureAvg: number | null
}

export interface DirectReportFilters {
  status?: string
  dateFrom?: number
  dateTo?: number
}

const PAGE_SIZE = 10

// ─── Query hooks ─────────────────────────────────────────────────────────────

/** Paginated personal assessments (I am the employee). */
export function useMyAssessments() {
  return usePaginatedQuery(
    api.assessments.listMyAssessments,
    {},
    { initialNumItems: PAGE_SIZE },
  )
}

/** Paginated direct-report assessments (I am the manager). */
export function useDirectReportAssessments(filters?: DirectReportFilters) {
  return usePaginatedQuery(
    api.assessments.listDirectReportAssessments,
    { filters: filters ?? {} },
    { initialNumItems: PAGE_SIZE },
  )
}

/** Paginated assessments for a specific employee — used on the employee's profile page. */
export function useAssessmentsForEmployee(employeeUserId: string | undefined) {
  return usePaginatedQuery(
    api.assessments.listAssessmentsForEmployee,
    employeeUserId ? { employeeUserId: employeeUserId as Id<'users'> } : 'skip',
    { initialNumItems: PAGE_SIZE },
  )
}

/** Single assessment — returns Convex-native shape. */
export function useConvexAssessment(assessmentId: string) {
  return useQuery(api.assessments.getAssessment, {
    assessmentId: assessmentId as Id<'assessments'>,
  })
}

/** Assessment with questions for the survey form — returns Convex-native shape. */
export function useConvexSurveyAssessment(assessmentId: string) {
  return useQuery(api.assessments.getAssessmentForSurvey, {
    assessmentId: assessmentId as Id<'assessments'>,
  })
}

/** Assessment averages by response type. */
export function useConvexAssessmentAverages(assessmentId: string) {
  return useQuery(api.assessments.getAssessmentAverages, {
    assessmentId: assessmentId as Id<'assessments'>,
  })
}

/**
 * Returns server-persisted draft answers for an incomplete assessment response.
 * Returns `undefined` while loading, `[]` if none saved or already completed.
 * Use this to restore progress cross-device or after localStorage is cleared.
 */
export function useConvexSavedAnswers(
  assessmentResponseId: string | undefined,
) {
  return useQuery(
    api.assessments.getSavedAnswers,
    assessmentResponseId
      ? {
          assessmentResponseId:
            assessmentResponseId as Id<'assessmentResponses'>,
        }
      : 'skip',
  )
}

/**
 * Returns all answers for an assessment response (completed or not).
 * Used by the Survey Result view to display per-question scores.
 * Pass `skip=true` to defer fetching until the user opens the accordion.
 */
export function useConvexAnswersForResponse(
  assessmentResponseId: string | undefined,
  skip = false,
) {
  return useQuery(
    api.assessments.getAnswersForResponse,
    !skip && assessmentResponseId
      ? {
          assessmentResponseId:
            assessmentResponseId as Id<'assessmentResponses'>,
        }
      : 'skip',
  )
}

/**
 * Returns a stable callback that saves the given answers as a checkpoint
 * (server-side draft). Fire-and-forget — errors are silently ignored since
 * the final submit re-sends all answers anyway.
 */
export function useConvexSaveCheckpoint() {
  const fn = useMutation(api.assessments.submitAnswers)
  return useCallback(
    (
      assessmentResponseId: string,
      answers: Record<string, number>,
      currentPage: number,
    ): void => {
      const payload = Object.entries(answers).map(
        ([questionId, answerValue]) => ({
          questionId: questionId as Id<'questions'>,
          answerValue,
        }),
      )
      if (payload.length === 0) return
      void fn({
        assessmentResponseId: assessmentResponseId as Id<'assessmentResponses'>,
        answers: payload,
        currentPage,
      })
    },
    [fn],
  )
}

// ─── Mutation hooks ──────────────────────────────────────────────────────────

/** Submit (upsert) answers for a response — matches the old mutateAsync interface. */
export function useConvexSubmitAnswers() {
  const fn = useMutation(api.assessments.submitAnswers)

  return {
    mutateAsync: ({
      assessmentResponseId,
      payload,
    }: {
      assessmentResponseId: string
      payload: { answers: Array<{ questionId: string; answerValue: number }> }
    }) =>
      fn({
        assessmentResponseId: assessmentResponseId as Id<'assessmentResponses'>,
        answers: payload.answers.map((a) => ({
          questionId: a.questionId as Id<'questions'>,
          answerValue: a.answerValue,
        })),
      }),
  }
}

/** Mark a response as complete — matches the old mutateAsync interface. */
export function useConvexCompleteResponse() {
  const fn = useMutation(api.assessments.completeResponse)
  return {
    mutateAsync: (assessmentResponseId: string) =>
      fn({
        assessmentResponseId: assessmentResponseId as Id<'assessmentResponses'>,
      }),
  }
}

/** Create an assessment for a single employee. */
export function useConvexCreateAssessment() {
  const fn = useMutation(api.assessments.createAssessment)
  return {
    mutateAsync: ({
      employeeUserId,
      organizationId,
    }: {
      employeeUserId: string
      organizationId: string
    }) =>
      fn({
        employeeUserId: employeeUserId as Id<'users'>,
        organizationId: organizationId as Id<'organizations'>,
      }),
  }
}

/** Create assessments for multiple employees. */
export function useConvexCreateBulkAssessments() {
  const fn = useMutation(api.assessments.createBulkAssessments)
  return {
    mutateAsync: ({
      employeeUserIds,
      organizationId,
    }: {
      employeeUserIds: string[]
      organizationId: string
    }) =>
      fn({
        employeeUserIds: employeeUserIds as Id<'users'>[],
        organizationId: organizationId as Id<'organizations'>,
      }),
  }
}
